import React, { useEffect, useState, useLayoutEffect } from 'react';
import { useSiteData } from '../../context/DataContext';
import { useLoadingContext } from '../Loading/LoadingProvider';
import Loading from '../Loading/Loading';
import { FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import ProductCard from '../ProductCard/ProductCard';
import { motion, AnimatePresence } from 'framer-motion';
import { useEntrance } from '../../context/EntranceContext';

function getRandomProducts(arr, n) {
    const shuffled = arr.slice().sort(() => 0.5 - Math.random());
    return shuffled.slice(0, n);
}

function Home() {
    const { startComponentLoading, stopComponentLoading, isComponentLoading } = useLoadingContext();
    const [isLoading, setIsLoading] = useState(true);
    const [randomProducts, setRandomProducts] = useState([]);

    const ITEMS_PER_PAGE = 8;
    const [currentPage, setCurrentPage] = useState(1);
    const { products } = useSiteData();
    const { hasEntered, setHasEntered } = useEntrance();

    // Reset entrance state on mount to ensure animation plays every time Home is visited
    // Removed the forced reset on mount to respect the context state
    // This effectively keeps the animation only for the first load (or refresh)
    /*
    useLayoutEffect(() => {
        setHasEntered(false);
        window.scrollTo(0, 0);
    }, [setHasEntered]);
    */

    useEffect(() => {
        const loadProducts = async () => {
            try {
                startComponentLoading('homeProducts', 'Loading products...');
                setIsLoading(true);
                await new Promise(resolve => setTimeout(resolve, 800));
                // Get 40 products for 5 pages to give more variety
                const randomProducts = getRandomProducts(products, 40);
                setRandomProducts(randomProducts);
            } catch (error) {
                console.error('Error loading products:', error);
                setRandomProducts(getRandomProducts(products, 40));
            } finally {
                setIsLoading(false);
                stopComponentLoading('homeProducts');
            }
        };
        loadProducts();
    }, [startComponentLoading, stopComponentLoading, products]);

    // Lock scroll when not entered
    // Lock scroll when not entered (Strict Mode)
    useLayoutEffect(() => {
        if (!hasEntered) {
            document.documentElement.style.overflow = 'hidden';
            document.body.style.overflow = 'hidden';
            document.body.style.height = '100vh';
        } else {
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
            document.body.style.height = '';
        }
        return () => {
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
            document.body.style.height = '';
        };
    }, [hasEntered]);

    // Pagination calculations
    const totalPages = Math.ceil(randomProducts.length / ITEMS_PER_PAGE);
    const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
    const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
    const currentItems = randomProducts.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => {
        if (pageNumber < 1 || pageNumber > totalPages) return;
        setCurrentPage(pageNumber);
        // Smooth scroll to the Trending section
        const trendingSection = document.getElementById('trending-now');
        if (trendingSection) {
            trendingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const onEnterGallery = () => {
        setHasEntered(true);
    };

    // Enable enter on scroll or touch move
    useEffect(() => {
        if (hasEntered) return;

        const handleScroll = () => {
            setHasEntered(true);
        };

        window.addEventListener('wheel', handleScroll, { passive: true });
        window.addEventListener('touchmove', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('wheel', handleScroll);
            window.removeEventListener('touchmove', handleScroll);
        };
    }, [hasEntered, setHasEntered]);

    // Allow clicking anywhere on the billboard
    const handleBillboardClick = () => {
        onEnterGallery();
    };



    return (
        <div className="home-wrapper" style={{ minHeight: '100vh', background: 'var(--bg-main)' }}>
            <AnimatePresence mode="wait">
                {!hasEntered ? (
                    <motion.section
                        key="entrance"
                        initial={{ opacity: 1 }}
                        exit={{
                            y: '-100%',
                            opacity: 0,
                            transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1] }
                        }}
                        onClick={handleBillboardClick}
                        className="hero-billboard"
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100vh',
                            zIndex: 1000,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 8%',
                            overflow: 'hidden',
                            background: '#0a0a0a',
                            cursor: 'pointer'
                        }}
                    >
                        <div className="billboard-content" style={{ position: 'relative', zIndex: 2, maxWidth: '700px' }}>
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 1, delay: 0.5 }}
                            >
                                <span style={{
                                    fontFamily: 'var(--font-nav)',
                                    fontSize: '11px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '8px',
                                    color: 'var(--accent-primary)',
                                    display: 'block',
                                    marginBottom: '20px'
                                }}>Curated Art Gallery</span>
                                <h1 style={{
                                    fontFamily: 'Cinzel, serif',
                                    fontSize: 'clamp(48px, 6vw, 84px)',
                                    fontWeight: '400',
                                    color: '#ffffff',
                                    lineHeight: '1',
                                    letterSpacing: '2px',
                                    marginBottom: '30px'
                                }}>
                                    Where Art meets <span style={{ color: 'var(--accent-primary)' }}>Vision.</span>
                                </h1>
                                <motion.button
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent double trigger
                                        onEnterGallery();
                                    }}
                                    whileHover={{ scale: 1.05, y: -3 }}
                                    whileTap={{ scale: 0.95 }}
                                    animate={{
                                        y: [0, -6, 0]
                                    }}
                                    transition={{
                                        y: {
                                            duration: 1.5,
                                            repeat: Infinity,
                                            ease: 'easeInOut'
                                        }
                                    }}
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        color: '#fff',
                                        padding: '18px 45px',
                                        fontFamily: 'var(--font-nav)',
                                        fontSize: '12px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '4px',
                                        cursor: 'pointer',
                                        transition: 'all 0.4s ease',
                                        position: 'relative',
                                        zIndex: 10,
                                        boxShadow: '0 4px 15px rgba(197, 160, 89, 0.15)'
                                    }}
                                    className="billboard-btn"
                                >
                                    Enter Gallery
                                </motion.button>
                            </motion.div>
                        </div>

                        <div className="billboard-visuals" style={{
                            flex: 1,
                            height: '100%',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end'
                        }}>
                            <motion.div
                                className="main-visual"
                                initial={{ opacity: 0, scale: 1.1 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 1.5, delay: 0.2 }}
                                style={{
                                    width: '100%',
                                    height: '80%',
                                    maxWidth: '800px',
                                    position: 'relative'
                                }}
                            >
                                <img
                                    src="/car logo.jpg"
                                    alt="Wallz Art"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        filter: 'grayscale(100%) brightness(0.6)',
                                        border: '1px solid rgba(255,255,255,0.05)'
                                    }}
                                />

                            </motion.div>
                        </div>
                    </motion.section>
                ) : (isLoading || isComponentLoading('homeProducts')) ? (
                    <motion.div
                        key="loader"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="home-loading"
                        style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-main)' }}
                    >
                        <Loading size="large" color="primary" text="Curating collection..." />
                    </motion.div>
                ) : (
                    <motion.div
                        key="gallery-content"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className="home-container"
                        style={{
                            minHeight: '100vh',
                            background: 'var(--bg-main)',
                            boxSizing: 'border-box'
                        }}
                    >
                        {/* Redesigned Gallery Hero (Visible after entry) */}
                        <section className="gallery-header-hero" style={{
                            width: '100%',
                            minHeight: '90vh',
                            position: 'relative',
                            padding: '0 8%',
                            display: 'flex',
                            alignItems: 'center',
                            background: '#0a0a0a',
                            overflow: 'hidden'
                        }}>
                            {/* Left Content */}
                            <div className="hero-text-content" style={{ flex: 1, zIndex: 2, paddingRight: '40px' }}>
                                <motion.span
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    style={{
                                        fontFamily: 'var(--font-nav)',
                                        fontSize: '12px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '6px',
                                        color: 'var(--accent-primary)',
                                        display: 'block',
                                        marginBottom: '40px',
                                        fontWeight: '600'
                                    }}
                                >
                                    Featured Collection
                                </motion.span>

                                <div style={{ marginBottom: '40px' }}>
                                    <motion.h1
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2 }}
                                        style={{
                                            fontFamily: 'var(--font-heading)',
                                            fontSize: 'clamp(50px, 8vw, 100px)',
                                            fontWeight: '800',
                                            color: '#ffffff',
                                            lineHeight: '0.9',
                                            textTransform: 'uppercase',
                                            margin: 0,
                                            letterSpacing: '-2px'
                                        }}
                                    >
                                        Modern
                                    </motion.h1>
                                    <motion.h1
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.4 }}
                                        style={{
                                            fontFamily: 'var(--font-heading)',
                                            fontSize: 'clamp(50px, 8vw, 100px)',
                                            fontWeight: '800',
                                            lineHeight: '0.9',
                                            textTransform: 'uppercase',
                                            margin: 0,
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            letterSpacing: '-2px'
                                        }}
                                    >
                                        <span style={{
                                            color: 'transparent',
                                            WebkitTextStroke: '1px rgba(255,255,255,0.4)',
                                            marginRight: '10px'
                                        }}>Vision</span>
                                        <span style={{ color: '#ffffff', fontSize: '0.9em' }}>aries</span>
                                    </motion.h1>
                                </div>

                                <motion.p
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.6 }}
                                    style={{
                                        fontFamily: 'var(--font-nav)',
                                        fontSize: '15px',
                                        color: 'rgba(255,255,255,0.4)',
                                        maxWidth: '400px',
                                        lineHeight: '1.7',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    Redefining wall art through bold typography, iconic visuals, and
                                    premium craftsmanship. Each piece is a statement.
                                </motion.p>
                            </div>

                            {/* Right Visual Composition */}
                            <div className="hero-visual-composition" style={{
                                flex: 1.2,
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end'
                            }}>
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
                                    style={{
                                        width: '95%',
                                        height: '75vh',
                                        position: 'relative',
                                        borderRadius: '24px',
                                        overflow: 'hidden',
                                        boxShadow: '0 50px 100px rgba(0,0,0,0.6)'
                                    }}
                                >
                                    <img
                                        src="/logo 2.jpg"
                                        alt="Wallz Design Composition"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        background: 'linear-gradient(45deg, rgba(10,10,10,0.4), transparent)'
                                    }}></div>
                                </motion.div>

                                {/* Background Decorative Glow */}
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    right: '-10%',
                                    width: '600px',
                                    height: '600px',
                                    background: 'var(--accent-primary)',
                                    filter: 'blur(180px)',
                                    opacity: 0.08,
                                    borderRadius: '50%',
                                    pointerEvents: 'none',
                                    zIndex: 0
                                }}></div>
                            </div>
                        </section>

                        <div className="brand-manifesto" style={{
                            maxWidth: '800px',
                            margin: '0 auto 120px',
                            textAlign: 'center',
                            padding: '0 20px'
                        }}>
                            <h2 style={{
                                fontFamily: 'var(--font-heading)',
                                fontSize: 'clamp(32px, 5vw, 56px)',
                                fontWeight: '400',
                                lineHeight: '1.2',
                                color: '#ffffff',
                                marginBottom: '15px',
                                letterSpacing: '2px',
                                textTransform: 'uppercase'
                            }}>
                                Elevate Your Space.
                            </h2>
                            <p style={{
                                fontFamily: 'var(--font-nav)',
                                fontSize: '14px',
                                color: 'var(--accent-primary)',
                                letterSpacing: '5px',
                                textTransform: 'uppercase',
                                fontWeight: '500'
                            }}>
                                Premium Wall Art Gallery
                            </p>
                        </div>

                        <div className="trending-section" style={{ maxWidth: '100%', margin: '0', padding: '0 30px' }} id="trending-now">
                            <h2 style={{
                                fontFamily: 'var(--font-heading)',
                                fontSize: '36px',
                                fontWeight: '400',
                                marginBottom: '60px',
                                color: '#ffffff',
                                textAlign: 'left',
                                letterSpacing: '0.5px'
                            }}>Trending Now</h2>

                            <motion.div
                                key={currentPage}
                                className="posters-grid"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(4, 1fr)',
                                    gap: '40px',
                                    margin: '0 auto'
                                }}
                                initial="hidden"
                                animate="visible"
                                variants={{
                                    hidden: { opacity: 0 },
                                    visible: {
                                        opacity: 1,
                                        transition: {
                                            staggerChildren: 0.1
                                        }
                                    }
                                }}
                            >
                                {currentItems.map((product) => (
                                    <ProductCard key={`${product.id}-${currentPage}`} product={product} />
                                ))}
                            </motion.div>

                            {/* Ultra-Premium Navigation */}
                            <div className="pagination-container" style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '30px',
                                marginTop: '120px',
                                paddingBottom: '40px'
                            }}>
                                <div style={{
                                    fontFamily: 'var(--font-nav)',
                                    fontSize: '10px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '6px',
                                    color: 'rgba(255,255,255,0.25)',
                                    fontWeight: '600'
                                }}>
                                    Discover More Art
                                </div>

                                <div className="nav-group" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '30px'
                                }}>
                                    <motion.button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        whileHover={{ x: -5, color: 'var(--accent-primary)' }}
                                        disabled={currentPage === 1}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: currentPage === 1 ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.4)',
                                            cursor: currentPage === 1 ? 'default' : 'pointer',
                                            fontSize: '20px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            transition: 'color 0.3s ease'
                                        }}
                                    >
                                        <FiArrowLeft />
                                    </motion.button>

                                    <div className="pagination-wrapper" style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '8px 20px',
                                        background: 'rgba(255,255,255,0.03)',
                                        borderRadius: '100px',
                                        border: '1px solid rgba(255,255,255,0.06)',
                                        backdropFilter: 'blur(15px)',
                                        boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                                    }}>
                                        {[...Array(totalPages)].map((_, i) => {
                                            const pageNum = i + 1;
                                            const isActive = currentPage === pageNum;

                                            return (
                                                <motion.div
                                                    key={pageNum}
                                                    style={{ position: 'relative' }}
                                                >
                                                    <motion.button
                                                        onClick={() => handlePageChange(pageNum)}
                                                        whileHover={{ scale: 1.1 }}
                                                        whileTap={{ scale: 0.9 }}
                                                        style={{
                                                            width: '38px',
                                                            height: '38px',
                                                            borderRadius: '50%',
                                                            border: 'none',
                                                            background: 'transparent',
                                                            color: isActive ? '#000' : 'rgba(255,255,255,0.4)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'pointer',
                                                            fontFamily: 'var(--font-nav)',
                                                            fontWeight: '700',
                                                            fontSize: '13px',
                                                            zIndex: 2,
                                                            position: 'relative',
                                                            transition: 'color 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
                                                        }}
                                                    >
                                                        {pageNum}
                                                    </motion.button>

                                                    {isActive && (
                                                        <motion.div
                                                            layoutId="active-pill"
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                backgroundColor: 'var(--accent-primary)',
                                                                borderRadius: '50%',
                                                                zIndex: 1,
                                                                boxShadow: '0 0 25px rgba(197, 160, 89, 0.5)'
                                                            }}
                                                            transition={{
                                                                type: 'spring',
                                                                stiffness: 400,
                                                                damping: 35
                                                            }}
                                                        />
                                                    )}
                                                </motion.div>
                                            );
                                        })}
                                    </div>

                                    <motion.button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        whileHover={{ x: 5, color: 'var(--accent-primary)' }}
                                        disabled={currentPage === totalPages}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: currentPage === totalPages ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.4)',
                                            cursor: currentPage === totalPages ? 'default' : 'pointer',
                                            fontSize: '20px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            transition: 'color 0.3s ease'
                                        }}
                                    >
                                        <FiArrowRight />
                                    </motion.button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <style>{`
        .billboard-btn {
            overflow: hidden;
            position: relative;
        }
        .billboard-btn::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(
                90deg,
                transparent,
                rgba(197, 160, 89, 0.3),
                transparent
            );
            animation: shimmer 3s infinite;
        }
        @keyframes shimmer {
            0% {
                left: -100%;
            }
            50%, 100% {
                left: 100%;
            }
        }
        .active-indicator {
            box-shadow: 0 0 15px var(--accent-primary);
        }
        @media (max-width: 1400px) {
            .posters-grid {
                grid-template-columns: repeat(3, 1fr) !important;
            }
        }
        @media (max-width: 1024px) {
            .home-container {
                margin-left: 0 !important;
                padding: 0 30px 80px !important;
            }
            .posters-grid {
                grid-template-columns: repeat(2, 1fr) !important;
                gap: 30px !important;
            }
        }
        @media (max-width: 768px) {
            .home-container {
                margin-left: 0 !important;
                padding: 0 0 100px !important;
            }
            .trending-section {
                padding: 0 10px !important;
            }
            .posters-grid {
                grid-template-columns: repeat(2, 1fr) !important;
                gap: 8px !important;
                padding: 0 5px !important;
            }
            .gallery-header-hero {
                flex-direction: column !important;
                justify-content: center !important;
                padding: 60px 20px !important;
                text-align: center !important;
                height: auto !important;
                min-height: 100vh !important;
            }
            .hero-billboard {
                flex-direction: column !important;
                justify-content: center !important;
                align-items: center !important;
                padding: 40px 20px !important;
                text-align: center !important;
                height: 100dvh !important;
                min-height: 100dvh !important;
                gap: 15px !important;
            }
            .hero-text-content {
                max-width: 100% !important;
                padding-right: 0 !important;
                margin-bottom: 20px !important;
                flex: 0 0 auto !important;
            }
            .hero-text-content h1 {
                justify-content: center !important;
                text-align: center !important;
            }
            .billboard-content {
                order: 2 !important;
                max-width: 100% !important;
                padding-right: 0 !important;
                margin-top: 0 !important;
                margin-bottom: 0 !important;
                flex: 0 0 auto !important;
            }
            .billboard-visuals, .hero-visual-composition {
                order: 1 !important;
                width: 100% !important;
                height: auto !important;
                justify-content: center !important;
                flex: 0 0 auto !important;
                margin-bottom: 0 !important;
            }
            .main-visual, .hero-visual-composition > div {
                height: 35vh !important;
                width: 100% !important;
                max-height: 350px !important;
            }
            .billboard-btn {
                padding: 15px 35px !important;
            }
            .hero-text-content h1 {
                font-size: 48px !important;
            }
        }
      `}</style>
        </div>
    );
}

export default Home;
